import React, { useState, useEffect, useCallback } from 'react';
import { DownloadSimple } from '@phosphor-icons/react';

// Capture the install prompt globally so it survives route changes.
// This runs once when the module loads – before any component mounts.
let _deferredPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  _deferredPrompt = e;
});

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(_deferredPrompt);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Detect iOS
    const ua = window.navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(isiOS);

    if (isiOS) {
      setShowBanner(true);
      return;
    }

    // If the event was already captured before this component mounted
    if (_deferredPrompt) {
      setDeferredPrompt(_deferredPrompt);
      setShowBanner(true);
      return;
    }

    // Listen for future events (in case component mounts before the event fires)
    const handler = (e) => {
      e.preventDefault();
      _deferredPrompt = e;
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    _deferredPrompt = null;
    setDeferredPrompt(null);
  }, [deferredPrompt, isIOS]);

  if (!showBanner) return null;

  return (
    <>
      {/* Install banner – always visible, no dismiss */}
      <div className="fixed bottom-0 inset-x-0 z-50 p-4 pb-[env(safe-area-inset-bottom,16px)] animate-bounce-subtle">
        <style>{`
          @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-6px); }
          }
          .animate-bounce-subtle {
            animation: bounce-subtle 3s ease-in-out infinite;
          }
          .animate-bounce-subtle:hover {
            animation-play-state: paused;
          }
        `}</style>
        <div
          className="max-w-md mx-auto bg-[#2D2A26] text-white rounded-2xl shadow-elevated p-4 flex items-center gap-3"
        >
          <div className="w-11 h-11 bg-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <DownloadSimple weight="bold" className="w-6 h-6" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm leading-tight">Install SmartPOS</p>
            <p className="text-white/60 text-xs mt-0.5">
              {isIOS
                ? 'Add to your home screen for the full app experience'
                : 'Install for instant access – works offline too'}
            </p>
          </div>

          <button
            onClick={handleInstall}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl transition-all duration-200 flex-shrink-0"
          >
            Install
          </button>
        </div>
      </div>

      {/* iOS share sheet guide overlay */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] bg-primaryB-900/70 flex items-end justify-center" onClick={() => setShowIOSGuide(false)}>
          <div
            className="bg-primaryB-50 rounded-t-3xl w-full max-w-md p-6 pb-[env(safe-area-inset-bottom,24px)] shadow-elevated"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-primaryB-300 rounded-full mx-auto mb-5" />
            <h3 className="text-lg font-bold text-[#2D2A26] mb-4">Install SmartPOS on iPhone</h3>

            <ol className="space-y-4 text-sm text-[#6B675F]">
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">1</span>
                <span>Tap the <strong className="text-[#2D2A26]">Share</strong> button <span className="inline-block align-middle text-blue-500">&#x2B06;</span> at the bottom of Safari</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">2</span>
                <span>Scroll down and tap <strong className="text-[#2D2A26]">"Add to Home Screen"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">3</span>
                <span>Tap <strong className="text-[#2D2A26]">"Add"</strong> – SmartPOS will appear on your home screen</span>
              </li>
            </ol>

            <button
              onClick={() => setShowIOSGuide(false)}
              className="mt-6 w-full bg-[#2D2A26] text-white py-3 rounded-xl font-bold transition-all duration-200"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
